import { useCallback, useEffect, useMemo, useState } from "react";
import type * as React from "react";
import AddIcon from "@mui/icons-material/Add";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Box, Button, Checkbox, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { postMembershipInvitesPaymentLink } from "apis/v3/authApi";
import GLEmailField, {
  normalizeEmailInput
} from "components/v3/forms/formComponents/GLEmailField";
import GLTextField from "components/v3/forms/formComponents/GLTextField";
import GiftCheckoutStepper from "components/v3/pages/account/GiftCheckoutStepper";
import GiftRecipientsCheckoutSidebar from "components/v3/pages/account/GiftRecipientsCheckoutSidebar";
import SideMenuLayout from "components/v3/pages/onboarding/SideMenuLayout";
import {
  formatUsdWithSpaceAfterSymbol,
  useAnimatedUsdString
} from "components/v3/sharedComponents/AnimatedUsdAmount";
import GLButton from "components/v3/sharedComponents/GLButton";
import GenomicLifeLegalDisclaimer from "components/v3/sharedComponents/GenomicLifeLegalDisclaimer";
import { COLORS } from "components/v3/Theme/colors";
import {
  GIFT_MAX_PLAN_QUANTITY,
  GIFT_UNIT_PRICE_USD
} from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import {
  trackGiftCheckoutLinkCompleted,
  trackGiftCheckoutLinkFailed
} from "analytics";
import PageTitle from "utils/PageTitle";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import { validateEmailString } from "utils/utils";

type GiftRecipient = {
  ageAcknowledged: boolean;
  confirmEmail: string;
  email: string;
  firstName: string;
  id: string;
  lastName: string;
};

type GiftRecipientsLocationState = {
  giftQuantity?: number;
};

function createEmptyRecipient(): GiftRecipient {
  return {
    id:
      typeof globalThis !== "undefined" && globalThis.crypto?.randomUUID
        ? globalThis.crypto.randomUUID()
        : `recipient-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
    ageAcknowledged: false
  };
}

const GiftRecipientsPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeState = location.state as GiftRecipientsLocationState | undefined;
  const giftQuantity = routeState?.giftQuantity ?? 1;

  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [recipients, setRecipients] = useState<GiftRecipient[]>(() => {
    const initial = [createEmptyRecipient()];
    for (
      let i = 1;
      i < Math.min(giftQuantity, GIFT_MAX_PLAN_QUANTITY);
      i += 1
    ) {
      initial.push(createEmptyRecipient());
    }
    return initial;
  });

  useEffect(() => {
    //window.scrollTo(0, 0);
  }, []);

  PageTitle("Gift recipients");

  const updateRecipient = useCallback(
    (index: number, patch: Partial<GiftRecipient>) => {
      setRecipients((prev) =>
        prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
      );
    },
    []
  );

  const addRecipient = useCallback(() => {
    setRecipients((prev) =>
      prev.length >= GIFT_MAX_PLAN_QUANTITY
        ? prev
        : [...prev, createEmptyRecipient()]
    );
  }, []);

  const removeRecipient = useCallback((index: number) => {
    setRecipients((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)
    );
  }, []);

  const canContinue = useMemo(() => {
    return recipients.every((r) => {
      const emailNorm = normalizeEmailInput(r.email);
      const confirmNorm = normalizeEmailInput(r.confirmEmail);
      const emailsOk =
        emailNorm.length > 0 &&
        confirmNorm.length > 0 &&
        emailNorm === confirmNorm &&
        validateEmailString(emailNorm);
      return (
        r.firstName.trim().length > 0 &&
        r.lastName.trim().length > 0 &&
        emailsOk &&
        r.ageAcknowledged
      );
    });
  }, [recipients]);

  const planCount = recipients.length;
  const animatedYearlyLabel = useAnimatedUsdString(
    planCount * GIFT_UNIT_PRICE_USD
  );

  const handleCheckout = () => {
    if (!canContinue || checkoutSubmitting) {
      return;
    }
    setCheckoutSubmitting(true);
    postMembershipInvitesPaymentLink({
      recipients: recipients.map((r) => ({
        email: normalizeEmailInput(r.email),
        firstName: r.firstName.trim(),
        lastName: r.lastName.trim()
      }))
    })
      .then((response) => {
        const link =
          response && "data" in response && response.data?.data?.ecommerceLink;
        if (link) {
          trackGiftCheckoutLinkCompleted({ recipientCount: planCount });
          history.push(PATHS.ACCOUNT_MANAGEMENT_GIFT_PAYMENT, {
            ecommerceLink: link,
            giftQuantity: planCount
          });
        } else {
          trackGiftCheckoutLinkFailed({ recipientCount: planCount });
          dispatch(
            openGLSnackbar({
              variant: "error",
              header: "",
              subText: "Something went wrong, please try again."
            })
          );
          setCheckoutSubmitting(false);
        }
      })
      .catch((error) => {
        trackGiftCheckoutLinkFailed({ recipientCount: planCount, error });
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "",
            subText: "Something went wrong, please try again."
          })
        );
        setCheckoutSubmitting(false);
      });
  };

  return (
    <SideMenuLayout
      onboardingStepper={
        <GiftCheckoutStepper activeStepIndex={0} giftCount={planCount} />
      }
      stepperPlacement="top"
    >
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: { lg: "row", xs: "column" },
          gap: 4,
          paddingX: { lg: 0, xs: 2 },
          paddingBottom: 3,
          width: "100%"
        }}
      >
        <Box
          sx={{
            bgcolor: COLORS.WHITE,
            borderRadius: "12px",
            boxSizing: "border-box",
            display: "flex",
            flex: 1,
            flexDirection: "column",
            gap: 3,
            minWidth: 0,
            p: { lg: 4, xs: 3 },
            pb: { lg: 0, xs: 0 }
          }}
        >
          <Typography
            component="h1"
            sx={{ typography: { xs: "h5_black", md: "h4_black" } }}
          >
            Who is the membership for?
          </Typography>

          <Divider sx={{ borderColor: alpha(COLORS.DARK_GRAY, 0.2), my: 2 }} />

          {recipients.map((recipient, index) => (
            <Box key={recipient.id}>
              {index > 0 ? <Divider sx={{ my: 3 }} /> : null}
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2
                }}
              >
                <Typography
                  sx={{ color: COLORS.DARK_GRAY }}
                  variant="body_strong_black"
                >
                  Recipient {index + 1}
                </Typography>
                {index > 0 ? (
                  <Button
                    sx={{
                      color: COLORS.PRIMARY,
                      fontWeight: 600,
                      minWidth: "auto",
                      textTransform: "none",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px"
                    }}
                    type="button"
                    onClick={() => removeRecipient(index)}
                  >
                    Remove
                  </Button>
                ) : null}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { md: "row", xs: "column" },
                  gap: 2,
                  mb: 2,
                  "& > *": { flex: 1, minWidth: 0 }
                }}
              >
                <GLTextField
                  required
                  id={`gift-recipient-${recipient.id}-firstName`}
                  label="First name"
                  value={recipient.firstName}
                  onChange={({ e }) =>
                    updateRecipient(index, { firstName: e.target.value })
                  }
                />
                <GLTextField
                  required
                  id={`gift-recipient-${recipient.id}-lastName`}
                  label="Last name"
                  value={recipient.lastName}
                  onChange={({ e }) =>
                    updateRecipient(index, { lastName: e.target.value })
                  }
                />
              </Box>

              <Box
                component="label"
                sx={{
                  alignItems: "flex-start",
                  cursor: "pointer",
                  display: "flex",
                  gap: 1,
                  mb: 2
                }}
              >
                <Checkbox
                  checked={recipient.ageAcknowledged}
                  onChange={() =>
                    updateRecipient(index, {
                      ageAcknowledged: !recipient.ageAcknowledged
                    })
                  }
                  sx={{ mt: -0.5, p: 0.5 }}
                />
                <Typography sx={{ pt: 0.5 }} variant="body_strong_black">
                  I confirm that this person is 18 years or older and is located
                  in the United States
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <GLEmailField
                  required
                  id={`gift-recipient-${recipient.id}-email`}
                  label="Recipient email"
                  value={recipient.email}
                  onChange={({ e }) =>
                    updateRecipient(index, { email: e.target.value })
                  }
                />
                <GLEmailField
                  required
                  id={`gift-recipient-${recipient.id}-confirmEmail`}
                  label="Confirm email address"
                  value={recipient.confirmEmail}
                  additionalValidation={(value = "") => {
                    const primary = recipient.email;
                    if (
                      value.length > 0 &&
                      primary.length > 0 &&
                      value !== primary
                    ) {
                      return "Email addresses must match";
                    }
                    return undefined;
                  }}
                  onChange={({ e }) =>
                    updateRecipient(index, { confirmEmail: e.target.value })
                  }
                />
              </Box>
            </Box>
          ))}

          <Divider sx={{ borderColor: alpha(COLORS.DARK_GRAY, 0.25), my: 1 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <GLButton
              buttonType="secondary-transparent"
              disabled={recipients.length >= GIFT_MAX_PLAN_QUANTITY}
              leftIcon={<AddIcon />}
              onClick={addRecipient}
              sx={{
                alignSelf: "flex-start",
                "&.Mui-disabled": {
                  borderColor: COLORS.GRAY,
                  color: COLORS.DARK_GRAY,
                  opacity: 1,
                  "& .MuiSvgIcon-root, & .MuiTypography-root": {
                    color: COLORS.DARK_GRAY
                  }
                }
              }}
              text="Add another recipient"
            />
            <Typography
              sx={{
                color:
                  recipients.length >= GIFT_MAX_PLAN_QUANTITY
                    ? COLORS.NOTICE
                    : COLORS.DARK_GRAY,
                fontStyle: "italic"
              }}
              variant="body2"
            >
              Maximum of {GIFT_MAX_PLAN_QUANTITY}.
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: COLORS.BACKGROUND_BLUE,
              borderRadius: "12px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              p: 4,
              pb: 2,
              width: "100%"
            }}
          >
            <Typography
              variant="body"
              sx={{
                mb: 1,
                textDecoration: "underline",
                textUnderlineOffset: "2px"
              }}
            >
              Gift details
            </Typography>

            <Typography
              component="p"
              sx={{ m: 0, mb: 1.5 }}
              variant="body_strong_black"
            >
              {planCount} gifted plan{planCount === 1 ? "" : "s"}
            </Typography>
            <Typography
              sx={{
                color: COLORS.DARK_GRAY_2,
                fontWeight: 700,
                lineHeight: 1.3,
                m: 0
              }}
              variant="body_strong_black"
            >
              x {formatUsdWithSpaceAfterSymbol(GIFT_UNIT_PRICE_USD)}
            </Typography>
            <Typography
              sx={{
                color: COLORS.DARK_GRAY,
                fontStyle: "italic",
                lineHeight: 1.35,
                mb: 2,
                mt: 0.25
              }}
              variant="body2"
            >
              Price per membership
            </Typography>

            <Typography sx={{ mb: 0 }} variant="body_black">
              Total price
            </Typography>
            <Typography
              sx={{ fontVariantNumeric: "tabular-nums", mb: 2 }}
              variant="body_strong_lg_accent2"
            >
              {animatedYearlyLabel}
            </Typography>
          </Box>
          <GLButton
            buttonType="primary"
            disabled={!canContinue || checkoutSubmitting}
            loading={checkoutSubmitting}
            onClick={handleCheckout}
            rightIcon={<ArrowForward />}
            sx={{ mb: 3 }}
            text="Checkout"
          />
        </Box>

        <GiftRecipientsCheckoutSidebar />
      </Box>
      <Box>
        <Divider
          sx={{
            borderBottomWidth: "3px",
            borderColor: COLORS.PRIMARY_LIGHTEST,
            my: { sm: 2, xs: 2 }
          }}
        />

        <GenomicLifeLegalDisclaimer
          textSx={{ color: COLORS.DARK_GRAY, m: 3 }}
          typographyVariant="body"
        />
      </Box>
    </SideMenuLayout>
  );
};

export default GiftRecipientsPage;
